import type {
  BackendWeekContent,
  SyllabusUnit,
  UnitWiseSyllabus,
  WeekPlanUI,
} from '../types/curriculum.types.ts';

let _idCounter = 1;
const uid = () => `id_${_idCounter++}_${Date.now()}`;

export function toBackendStudyPlanContent(weekPlans: WeekPlanUI[]) {
  return {
    weeks: weekPlans.map((wp) => ({
      week: wp.week,
      theme: wp.theme,
      topics: wp.topics,
      classWork: wp.classWork,
      homework: wp.homework,
      quiz: wp.quiz,
      resources: wp.resources,
      marks: wp.marks,
      description: wp.description,
      days: [
        { topic: wp.theme },
        ...wp.topics.map((t) => ({ topic: t })),
      ],
    })),
  };
}

export function fromBackendStudyPlanContent(content: {
  weeks?: BackendWeekContent[];
} | null | undefined): WeekPlanUI[] {
  if (!content?.weeks?.length) return [];

  return content.weeks.map((w, idx) => {
    if (w.theme || w.topics || w.classWork) {
      return {
        week: w.week ?? idx + 1,
        theme: w.theme ?? w.days?.[0]?.topic ?? `Week ${idx + 1}`,
        topics: w.topics ?? w.days?.slice(1).map((d) => d.topic).filter(Boolean) ?? [],
        classWork: w.classWork ?? [],
        homework: w.homework ?? [],
        quiz: w.quiz ?? { type: 'Objective', questions: 0, duration: '20 min' },
        resources: w.resources ?? [],
        marks: w.marks ?? 0,
        description: w.description,
      };
    }

    const days = w.days ?? [];
    const theme = days[0]?.topic ?? `Week ${idx + 1}`;
    return {
      week: idx + 1,
      theme,
      topics: days.slice(1).map((d) => d.topic).filter(Boolean),
      classWork: [],
      homework: [],
      quiz: { type: 'Objective', questions: 0, duration: '20 min' },
      resources: [],
      marks: 0,
    };
  });
}

export function unitsToSyllabusPayload(
  units: SyllabusUnit[],
  knowledgeMapId: string,
  metadata: { board: string; class: string; subject: string; status?: string; terms?: unknown[] }
) {
  return {
    knowledge_map_id: knowledgeMapId,
    board: metadata.board,
    class: metadata.class,
    subject: metadata.subject,
    units,
    status: metadata.status ?? 'draft',
    ...(metadata.terms ? { terms: metadata.terms } : {}),
  };
}

export function syllabusToUnits(doc: UnitWiseSyllabus | null | undefined): SyllabusUnit[] | null {
  if (!doc?.units?.length) return null;
  return doc.units.map((unit) => ({
    id: unit.id || uid(),
    name: unit.name,
    topics: (unit.topics ?? []).map((topic) => ({
      id: topic.id || uid(),
      topic: topic.topic,
      description: topic.description,
      subtopics: (topic.subtopics ?? []).map((st) => ({
        id: st.id || uid(),
        subtopic: st.subtopic,
        marks: st.marks ?? 0,
      })),
    })),
  }));
}
